"use client";

import { ArrowRight, FileText, Leaf, Scale } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  icon: Scale,
  title: "Integrity",
  lineOne: "Unwavering commitment to ethical trading and fair market practices.",
  lineTwo: "Verified supplier audits ensuring compliance with global standards."
}, {
  icon: FileText,
  title: "Efficiency",
  lineOne: "Streamlined procurement workflows that minimize administrative overhead.",
  lineTwo: "Automated documentation and real-time tracking for every transaction."
}, {
  icon: Leaf,
  title: "Sustainability",
  lineOne: "Promoting circular economy practices within the global supply chain.",
  lineTwo: "Data-driven insights to reduce waste and optimize resource allocation."
}];
export default function Vision() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="15926" id="our-vision" className="bg-white text-slate-900 py-32 md:py-44">
      <div className="container mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className="lg:sticky lg:top-32 transition-all duration-[620ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" style={{
            opacity: leftVisible ? 1 : 0,
            transform: leftVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <p className="text-xs font-semibold uppercase text-blue-900 tracking-[0.16em] font-sans">
                The KTEX Vision
              </p>
              <div className="mt-4 h-px bg-blue-900 transition-all duration-[520ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" style={{
              width: leftVisible ? "48px" : "0px"
            }} />

              <h2 className="mt-8 font-sans font-semibold text-slate-900 leading-[1.08]" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
              letterSpacing: "-0.018em"
            }}>
                A trading platform <em className="font-serif italic">engineered</em> for global growth.
              </h2>

              <div className="mt-8 space-y-5 font-sans text-slate-600 leading-[1.6]" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              letterSpacing: "-0.005em"
            }}>
                <p>
                  KTEX was founded to bridge the gap between global suppliers and 
                  buyers through a secure, transparent, and high-performance marketplace.
                </p>
                <p>
                  We combine advanced digital infrastructure with deep industry 
                  expertise to simplify complex international trade requirements.
                </p>
                <p>
                  Our mission is to empower businesses to scale with confidence, 
                  backed by a network built on trust and operational excellence.
                </p>
              </div>

              <div className="mt-8">
                <Link to="#our-mission" className="group inline-flex items-center gap-2 font-sans text-sm font-medium text-blue-900">
                  <span className="relative pb-1">
                    Learn about our mission
                    <span className="absolute left-0 bottom-0 h-px w-full bg-blue-900 transition-all duration-200 group-hover:h-[2px]" />
                  </span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="border-t border-slate-200">
              {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return <PillarRow key={pillar.title} data-index={i} index={i} Icon={Icon} title={pillar.title} lineOne={pillar.lineOne} lineTwo={pillar.lineTwo} />;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type PillarRowProps = {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  lineOne: string;
  lineTwo: string;
  "data-index"?: number;
};
function PillarRow({
  index,
  Icon,
  title,
  lineOne,
  lineTwo,
  ...rest
}: PillarRowProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className="group grid grid-cols-12 gap-6 py-10 md:py-12 border-b border-slate-200 transition-colors duration-200 hover:border-blue-900/30" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 620ms cubic-bezier(0.22,0.61,0.36,1) ${index * 80}ms, transform 620ms cubic-bezier(0.22,0.61,0.36,1) ${index * 80}ms, border-color 200ms ease-out`
  }}>
      <div className="col-span-2 flex items-start justify-start pt-1">
        <Icon className="h-8 w-8 text-blue-900 transition-colors duration-200 group-hover:text-blue-700" strokeWidth={1.25} />
      </div>
      <div className="col-span-10">
        <h3 className="font-sans font-default font-semibold text-slate-900 leading-[1.35]" style={{
        fontSize: "clamp(1rem, 1.1vw, 1.125rem)"
      }}>
          {title}
        </h3>
        <div className="mt-3 space-y-1.5 font-sans text-base text-slate-600 leading-[1.65]">
          <p>{lineOne}</p>
          <p>{lineTwo}</p>
        </div>
      </div>
    </div>;
}